/**
 * Staff_Service.ts
 * SK People employees, reached through this app's own adapter (/v1/staff),
 * which proxies to backbone's People gateway. These back the section "server"
 * picker — a server IS an SK People employee; hospitality only references one
 * by id (never stores the staff record itself). Same pattern as guests.ts
 * (CRM contacts) — every cross-product call goes through the adapter, never
 * straight to backbone (no public ingress / allow-listed origin for that).
 */

import { apiClient } from '../../api/axiosInstance';
import { Hosp_Staff_Url } from '../../api/endpoints';
import { newIdempotencyKey } from '../../lib/idempotency';

/** A server option for the dropdown — id is the SK People employee id (stored as serverRef). */
export interface Staff {
  id: string;
  name: string;
}

/** List employees for the picker (active staff). The adapter already
 *  normalizes sk-people's { items, total } wrapper down to a bare
 *  Staff[] (see staff.service.ts) — no further unwrapping here. */
export const listStaff = async (): Promise<Staff[]> => {
  const { data } = await apiClient.get<Staff[]>(Hosp_Staff_Url, {
    params: { status: 'active', limit: 100 },
  });
  return data ?? [];
};

/**
 * Create a server as an SK People employee (name-only, lightest UX). The record
 * lives in SK People; we auto-fill the fields it requires so a floor manager
 * types just a name: employeeNo = <ORG3>-<timestamp>, employmentType full_time,
 * joinDate today. Returns the created employee's id (to store as serverRef).
 */
export const createStaff = async (
  firstName: string,
  lastName: string,
  orgName: string,
): Promise<Staff> => {
  const orgPrefix = (orgName || 'STAFF').replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase() || 'STF';
  const employeeNo = `${orgPrefix}-${Date.now()}`;
  const joinDate = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const { data } = await apiClient.post<Staff>(
    Hosp_Staff_Url,
    { employeeNo, firstName, lastName: lastName || undefined, employmentType: 'full_time', joinDate },
    { headers: { 'Idempotency-Key': newIdempotencyKey('emp') } },
  );
  return data;
};
